<!--
 *@Description: 分类文章列表 - SEO友好的路径形式，支持无限滚动
-->
<script setup lang="ts">
const config = useRuntimeConfig()
const route = useRoute()

interface Article {
  ArticleId: number
  Title: string
  Slug: string
  Summary: string | null
  CoverImage: string | null
  CoverBlurHash: string | null
  TypeId: number
  TypeName?: string
  SeriesId: number | null
  IsPublic: number
  WordCount: number | null
  CreatedAt: string
  UpdatedAt: string
}

interface ApiResponse {
  articles: Article[]
  total: number
}

const typeSlug = computed(() => route.params.type as string)

// 无限滚动（带 30 分钟缓存）
const {
  items: articles,
  total,
  isLoading,
  isLoadingMore,
  hasMore,
  error,
  loadInitial,
  refresh
} = useInfiniteScroll<Article>({
  cacheKey: `blog-type-${typeSlug.value}`,
  fetchFn: async (offset, limit) => {
    const result = await $fetch<ApiResponse>(`${config.public.apiBase}/articles`, {
      query: { limit, offset, typeSlug: typeSlug.value }
    })
    return { items: result.articles, total: result.total }
  },
  limit: 20,
  threshold: 300
})

// SSR: 初始加载
await loadInitial()

// 从文章中获取分类名称（如果有）
const typeName = computed(() => {
  const list = articles.value
  if (list.length > 0 && list[0]?.TypeName) {
    return list[0].TypeName
  }
  return typeSlug.value
})

// 工具函数
const getImageUrl = (key: string) => {
  if (!key) return ''
  if (key.startsWith('http')) return key
  return `https://image.lixiang.page/${key}`
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 计算阅读时间（按每分钟 400 字计算）
const getReadingTime = (wordCount: number | null) => {
  if (!wordCount || wordCount <= 0) return '约 1 分钟'
  const minutes = Math.ceil(wordCount / 400)
  return `约 ${minutes} 分钟`
}

// SEO
useHead({
  title: `${typeName.value} - 博客`,
  meta: [
    { name: 'description', content: `浏览「${typeName.value}」分类下的所有文章` }
  ]
})

// 结构化数据
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${typeName.value} - 文章列表`,
        description: `${typeName.value}分类下的所有文章`,
        url: `https://lixiang.page/blog/type/${typeSlug.value}`
      })
    }
  ]
})
</script>

<template>
  <div class="blog-container">
    <!-- 分类标题栏 -->
    <div class="filter-header">
      <div class="filter-info">
        <h1 class="filter-title">{{ typeName }}</h1>
        <span v-if="total > 0" class="total-count">共 {{ total }} 篇</span>
      </div>
      <NuxtLink to="/blog" class="clear-filter">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
        查看全部
      </NuxtLink>
    </div>

    <div v-if="isLoading" class="loading">
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
        <path fill="#5B6FB5" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur=".8s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>

    <div v-else-if="error" class="error">
      <p>加载失败，请稍后重试</p>
      <button @click="() => refresh()">重试</button>
    </div>

    <div v-else>
      <div v-if="articles.length === 0" class="empty-state">
        <p>该分类暂无文章</p>
        <NuxtLink to="/blog" class="back-link">查看全部文章</NuxtLink>
      </div>

      <div v-else class="article-list">
        <NuxtLink v-for="article in articles" :key="article.ArticleId" :to="`/blog/${article.Slug}`" class="article-item">
          <div class="article-content">
            <h2 class="article-title">
              <span v-if="article.IsPublic === 0" class="lock-icon-inline" title="私密">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
                </svg>
              </span>
              {{ article.Title }}
            </h2>
            <p class="article-summary">{{ article.Summary || '暂无摘要' }}</p>
            <div class="article-meta-footer">
              <span class="meta-date">{{ formatDate(article.CreatedAt) }}</span>
              <span class="meta-separator">·</span>
              <span class="meta-read-time">{{ getReadingTime(article.WordCount) }}</span>
            </div>
          </div>

          <div class="article-image-wrapper">
            <img 
              v-if="article.CoverImage" 
              :src="getImageUrl(article.CoverImage)"
              :alt="article.Title"
              class="cover-image"
              loading="lazy"
            />
            <div v-else class="placeholder-image">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" style="color: #e5e5e5;">
                <rect x="3" y="3" width="18" height="18" rx="0" ry="0" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
            <div v-if="article.IsPublic === 0" class="lock-badge-mini" title="私密文章">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-3">
                <path d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" />
              </svg>
            </div>
          </div>
        </NuxtLink>

        <!-- 加载更多状态 -->
        <div v-if="isLoadingMore" class="loading-more">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 50 50">
            <path fill="#5B6FB5" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
              <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur=".8s" repeatCount="indefinite" />
            </path>
          </svg>
          <span>加载更多...</span>
        </div>

        <!-- 没有更多 -->
        <div v-else-if="!hasMore && articles.length > 0" class="no-more">
          已加载全部文章
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.blog-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
}

.filter-info {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.filter-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
}

.total-count {
  font-size: 0.875rem;
  color: #999;
}

.clear-filter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  color: #666;
  text-decoration: none;
  padding: 6px 12px;
  background: var(--bg-color);
  border: 1px solid #eaeaea;
  transition: all 0.2s ease;
}

.clear-filter:hover {
  color: #333;
  border-color: #ccc;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #666;
}

.empty-state p {
  font-size: 1.125rem;
  margin-bottom: 16px;
}

.back-link {
  color: var(--accent-color, #5B6FB5);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

.loading, .error {
  text-align: center;
  padding: 60px;
  color: #888;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #666;
  font-size: 0.875rem;
}

.no-more {
  text-align: center;
  padding: 24px;
  color: #999;
  font-size: 0.875rem;
}

.article-list {
  display: flex;
  flex-direction: column;
}

.article-item {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 1rem 0;
  border-bottom: 1px solid #eaeaea;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
}

.article-item:last-child {
  border-bottom: none;
}

.article-item:hover {
  opacity: 0.95;
}

.article-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.article-image-wrapper {
  order: -1;
  width: 100%;
  aspect-ratio: 16/9;
  position: relative;
  background: #f9f9f9;
  overflow: hidden;
  flex-shrink: 0;
}

.placeholder-image {
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s;
}

.article-item:hover .cover-image {
  opacity: 0.9;
}

.lock-badge-mini {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.article-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text-color);
  margin: 0 0 10px 0;
  letter-spacing: -0.01em;
  border-bottom: none;
}

.lock-icon-inline {
  display: inline-flex;
  vertical-align: middle;
  width: 20px;
  height: 20px;
  color: #888;
  margin-right: 4px;
}

.article-summary {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-meta-footer {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #999;
}

.meta-separator {
  margin: 0 8px;
}

@media (min-width: 768px) {
  .article-item {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    padding-bottom: 1.5rem;
  }

  .article-content {
    order: 1;
    padding-right: 40px;
  }

  .article-image-wrapper {
    order: 2;
    width: 240px;
    height: 160px;
    aspect-ratio: auto;
  }
}
</style>
